import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Divider, InputBase, Paper } from "@mui/material";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import CustomBotIcon from "../../../app/assets/custom-icons/CustomBotIcon";
import CoreButtonIcon from "../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import CoreSpinner from "../../../app/core/components/CoreSpinner/CoreSpinner";
import { hexToRgba } from "../../../utils/functions/hexToRgba";
import { useGetBotIdQuery } from "../../api/admin/chatbotApi";
import MessageTabs from "./MessageTabs";

const ChatBot = ({
  idBot,
  conversation,
  chatHistory,
  setChatHistory,
  onMessageSent,
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const colorPrimary = useSelector((state) => state.app.primaryColor);
  const rgbaColor = hexToRgba(colorPrimary, 0.3);
  const token = useSelector((state) => state.app.accessToken);
  const [text, setText] = useState("");
  const [bot, setBot] = useState();
  const [currentSources, setCurrentSources] = useState([]);
  const [partialAssistantMessage, setPartialAssistantMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(
    conversation?.conversationId || null
  );
  const [lastLoadedConversationId, setLastLoadedConversationId] =
    useState(null);

  const { data: dataBot } = useGetBotIdQuery(idBot, { skip: !idBot });

  useEffect(() => {
    // Scrolla verso il basso quando i messaggi cambiano
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [chatHistory, partialAssistantMessage]);

  useEffect(() => {
    if (dataBot && dataBot.agent) {
      setBot(dataBot.agent);
    }
  }, [dataBot]);

  useEffect(() => {
    // Aggiorna chatHistory SOLO se cambia la conversazione archiviata
    if (
      conversation &&
      Array.isArray(conversation.messages) &&
      conversation.conversationId !== lastLoadedConversationId
    ) {
      setChatHistory(conversation.messages);
      setConversationId(conversation.conversationId || null);
      setLastLoadedConversationId(conversation.conversationId || null);
    } else if (!conversation) {
      setConversationId(null);
      setLastLoadedConversationId(null);
    }
  }, [conversation, setChatHistory, lastLoadedConversationId]);

  const handleChangeText = (e) => {
    setText(e.target.value);
  };

  const handleSendMessage = async () => {
    if (text === "" || isStreaming) return;
    const userMessage = {
      role: "user",
      content: text,
      timestamp: null,
      sources: [], // I messaggi utente non hanno fonti
    };
    const updatedHistoryUser = [...chatHistory, userMessage];

    setChatHistory(updatedHistoryUser);
    setText("");
    setPartialAssistantMessage("");
    setCurrentSources([]);
    setIsStreaming(true);

    const bodyData = {
      agentId: idBot,
      newMessage: userMessage.content,
      ...(conversationId && { conversationId }),
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/admin/chatbot/chatbot`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bodyData),
      }
    );

    if (!response.body) {
      setIsStreaming(false);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let lastContent = "";
    let assistantReceived = false;
    let messageSources = []; // Fonti per questo specifico messaggio

    // Funzione per processare un JSON completo
    const processJsonObject = (obj) => {
      // Salva subito il conversationId se arriva come meta-event
      if (obj.event === "meta" && obj.conversationId) {
        setConversationId(obj.conversationId);
      }

      // Salva le fonti quando arriva l'evento sourceDocuments
      if (obj.event === "sourceDocuments" && obj.documents !== undefined) {
        messageSources = obj.documents; // Salva le fonti per questo messaggio
        setCurrentSources(obj.documents);
      }

      if (obj.role === "assistant") {
        setPartialAssistantMessage(obj.content);
        lastContent = obj.content;
        assistantReceived = true;
      }
    };

    // Funzione per estrarre JSON completi dal buffer
    const extractCompleteJsons = (buffer) => {
      const completeJsons = [];
      let remainingBuffer = buffer;
      let braceCount = 0;
      let startIndex = -1;
      let inString = false;
      let escapeNext = false;

      for (let i = 0; i < remainingBuffer.length; i++) {
        const char = remainingBuffer[i];

        if (escapeNext) {
          escapeNext = false;
          continue;
        }

        if (char === "\\" && inString) {
          escapeNext = true;
          continue;
        }

        if (char === '"') {
          inString = !inString;
          continue;
        }

        if (inString) continue;

        if (char === "{") {
          if (braceCount === 0) {
            startIndex = i;
          }
          braceCount++;
        } else if (char === "}") {
          braceCount--;
          if (braceCount === 0 && startIndex !== -1) {
            // Abbiamo un JSON completo
            const jsonStr = remainingBuffer.substring(startIndex, i + 1);
            try {
              const obj = JSON.parse(jsonStr);
              completeJsons.push(obj);
            } catch (e) {
              console.log("Error parsing extracted JSON:", e, "JSON:", jsonStr);
            }
            startIndex = -1;
          }
        }
      }

      // Ritorna i JSON completi e il buffer rimanente
      const lastCompleteIndex =
        startIndex === -1 ? remainingBuffer.lastIndexOf("}") + 1 : startIndex;

      return {
        completeJsons,
        remainingBuffer: remainingBuffer.substring(lastCompleteIndex),
      };
    };

    let done = false;
    while (!done) {
      const { done: doneReading, value } = await reader.read();
      done = doneReading;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Estrai e processa tutti i JSON completi
      const { completeJsons, remainingBuffer } = extractCompleteJsons(buffer);
      buffer = remainingBuffer;

      completeJsons.forEach(processJsonObject);
    }

    // Processa l'eventuale JSON rimasto nel buffer finale
    if (buffer.trim()) {
      const { completeJsons } = extractCompleteJsons(buffer);
      completeJsons.forEach(processJsonObject);
    }

    // Aggiungi solo se c'è stato almeno un messaggio assistant
    if (assistantReceived && lastContent) {
      setChatHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          content: lastContent,
          timestamp: null,
          sources: messageSources, // Salva le fonti associate a questo messaggio
        },
      ]);
    }

    setPartialAssistantMessage("");
    setCurrentSources([]);
    setIsStreaming(false);

    if (onMessageSent) onMessageSent();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="w-full h-full">
      <div
        style={{ height: "85%" }}
        className="overflow-auto"
        ref={messagesContainerRef}
      >
        <div className="flex items-center gap-x-2 p-4">
          <div className="flex justify-center items-center">
            <CustomBotIcon className="w-24" shadows={false} />
          </div>
          <div className="p-2">
            <p className="text-md m-0">
              <span className="font-bold" style={{ color: colorPrimary }}>
                {t("Intro1")}
              </span>{" "}
              {bot ? (
                <>
                  <span>{t("Intro2")}</span> {"Il mio nome è "}
                  <span className="font-bold" style={{ color: colorPrimary }}>
                    {bot?.name}{" "}
                  </span>{" "}
                </>
              ) : (
                t("Intro2")
              )}
            </p>
            <p
              className="font-bold m-0 text-md"
              style={{ color: colorPrimary }}
            >
              {t("WriteAMessageToBegin")}
            </p>
          </div>
        </div>
        <Divider />
        <div className="p-6 flex flex-col space-y-6">
          {chatHistory.map((message, index) => (
            <MessageTabs
              key={index}
              message={message}
              sources={message.sources || []}
              rgbaColor={rgbaColor}
            />
          ))}

          {/* Mostra la risposta in streaming */}
          {isStreaming && partialAssistantMessage && (
            <MessageTabs
              message={{ role: "assistant", content: partialAssistantMessage }}
              sources={currentSources}
              rgbaColor={rgbaColor}
            />
          )}
          {isStreaming && <CoreSpinner />}
        </div>
        <div ref={messagesEndRef} />
      </div>
      <Divider />

      <div
        className="flex justify-center items-center"
        style={{ height: "15%" }}
      >
        <Paper
          component="form"
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: "90%",
            borderRadius: "16px",
            border: "solid 1px lightgrey",
          }}
          elevation={0}
        >
          <InputBase
            multiline
            maxRows={4}
            disabled={isStreaming}
            sx={{ ml: 1, flex: 1 }}
            placeholder={t("WriteAMessage")}
            value={isStreaming ? "" : text}
            onChange={handleChangeText}
            onKeyDown={handleKeyDown}
          />
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <CoreButtonIcon
            disabled={isStreaming || !text}
            icon={<SendRoundedIcon />}
            onClick={handleSendMessage}
          />
        </Paper>
      </div>
    </div>
  );
};

export default ChatBot;
